import React from 'react';
import { Box, useTheme } from '@mui/material';

import { Button, Dialog, Switch, Text } from 'components';
import { useToast } from 'core/toast/hooks';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { useTranslations } from 'hooks/useTranslations';

import { UseMutation, UseQuery } from 'useQuery';
// eslint-disable-next-line no-restricted-imports
import { MobilityProduct } from 'useQuery/useOrganisations';

const MOBILITY_PRODUCT_SLUGS = {
  Mywheels: 'MYWHEELS',
  Greenwheels: 'GREENWHEELS_RNP',
  Check: 'CHECK',
} as const;

interface MobilityProductBlockProps {
  organisationId: string;
  slug: string;
  title: string;
  mobilityProduct: MobilityProduct | undefined;
  showWarningOnEnable?: boolean;
}

function MobilityProductBlock({
  organisationId,
  slug,
  title,
  mobilityProduct,
  showWarningOnEnable,
}: MobilityProductBlockProps) {
  const translations = useTranslations('mobilityProductsBlock');
  const { addToast } = useToast();
  const theme = useTheme();
  const { hasMobileView } = useHasMobileView();

  const [enabled, setEnabled] = React.useState(mobilityProduct?.enabled ?? false);
  const [enabledForAllUsers, setEnabledForAllUsers] = React.useState(
    mobilityProduct?.enabled_for_all_users ?? false,
  );
  const [openEnableWarning, setOpenEnableWarning] = React.useState(false);

  const { refetch: refetchOrganisationData, isLoading: loadingOrganisationData } =
    UseQuery.organisation({ id: organisationId });
  const { mutateAsync: mutateAsyncUpdateMobilityProduct, isLoading: loadingUpdateMobilityProduct } =
    UseMutation.updateOrganisationMobilityProduct();

  React.useEffect(() => {
    setEnabled(mobilityProduct?.enabled ?? false);
    setEnabledForAllUsers(mobilityProduct?.enabled_for_all_users ?? false);
  }, [mobilityProduct]);

  function handleEnabledChange(value: boolean) {
    if (showWarningOnEnable && value && !enabled) {
      setOpenEnableWarning(true);
    } else {
      setEnabled(value);
    }
  }

  const isUnchanged =
    enabled === (mobilityProduct?.enabled ?? false) &&
    enabledForAllUsers === (mobilityProduct?.enabled_for_all_users ?? false);

  const isLoading = loadingUpdateMobilityProduct || loadingOrganisationData;
  const disabledSave = isUnchanged || isLoading;

  async function save() {
    try {
      await mutateAsyncUpdateMobilityProduct({
        organisationId,
        slug,
        enabled,
        enabled_for_all_users: enabledForAllUsers,
      });
      await refetchOrganisationData();
      addToast({ title: translations.updatedSuccessfully, severity: 'success' });
    } catch (error) {
      addToast({ error, severity: 'error' });
    }
  }

  return (
    <>
      <Dialog
        title={translations.mywheelsWarningTitle}
        open={openEnableWarning}
        onChange={setOpenEnableWarning}
        footer={[
          {
            variant: 'outlined',
            label: translations.cancel,
            onClick: () => setOpenEnableWarning(false),
            disabled: false,
          },
          {
            variant: 'contained',
            label: translations.ok,
            onClick: () => {
              setEnabled(true);
              setOpenEnableWarning(false);
            },
            disabled: false,
          },
        ]}
      >
        <Text>{translations.mywheelsWarningMessage}</Text>
      </Dialog>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          padding: 2,
          marginBottom: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
        }}
      >
        <Text.Bold>{title}</Text.Bold>
        <Box sx={{ marginTop: 2 }}>
          <Switch label={translations.enabled} checked={enabled} onChange={handleEnabledChange} />
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <Switch
            label={translations.enabledForAllUsers}
            checked={enabledForAllUsers}
            onChange={setEnabledForAllUsers}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
          <Button
            label={translations.save}
            disabled={disabledSave}
            loading={loadingUpdateMobilityProduct}
            onClick={save}
          />
        </Box>
      </Box>
    </>
  );
}

interface OrganisationMobilityProductsProps {
  organisationId: string;
  mobilityProducts: MobilityProduct[];
}

function OrganisationMobilityProducts({
  organisationId,
  mobilityProducts,
}: OrganisationMobilityProductsProps) {
  const translations = useTranslations('mobilityProductsBlock');

  return (
    <>
      {Object.entries(MOBILITY_PRODUCT_SLUGS).map(([label, slug]) => (
        <MobilityProductBlock
          key={slug}
          organisationId={organisationId}
          slug={slug}
          title={translations[label as keyof typeof MOBILITY_PRODUCT_SLUGS]}
          mobilityProduct={mobilityProducts.find((p) => p.slug === slug)}
          showWarningOnEnable={slug === 'MYWHEELS'}
        />
      ))}
    </>
  );
}

export { OrganisationMobilityProducts };
